import { z } from '../zod.js';
import { AddressValidationSchema } from '../shared/addressValidationSchema.js';
import { FieldElementValidationEnum, FieldTypeValidationEnum } from '../field/enum.js';
import {
  ChargePaymentOptionTypeValidationEnum,
  ChargePaymentOptionManualTypeValidationEnum,
  ChargeStatusValidationEnum,
  ChargeAbandonmentEmailStatusValidationEnum,
  ChargeOrderStatusValidationEnum,
  CHARGE_ORDER_STATUS,
} from './enum';
import { PaymentMethodValidationSchema } from '../shared/paymentMethodValidationSchema.js';
import {
  HistoricalTaxRateSnapshotValidationSchema,
  TaxSourceValidationSchema,
  TAX_SOURCE_FIELD_DESCRIPTION,
  TAX_RATES_FIELD_DESCRIPTION,
} from '../tax-rate/create.js';

export const DiscountValidationSchema = z.object({
  minQuantity: z.number().nullish().meta({
    description: 'Minimum quantity required to be eligible for the discount.',
  }),
  maxQuantity: z.number().nullish().meta({
    description: 'Maximum quantity allowed for the discount.',
  }),
  percentOff: z.number().nullish().meta({
    description: 'Percentage off the original price. Cannot be combined with `amountOff`.',
  }),
  amountOff: z.number().nullish().meta({
    description: 'Fixed amount off the original price. Cannot be combined with `percentOff`.',
  }),
  amount: z.number().nullish().meta({
    description: 'Total discount amount.',
  }),
});

const VariantSelectedOptionSchema = z.object({
  optionId: z.string().nullish(),
  optionName: z.string().nullish(),
  sku: z.string().nullish(),
  quantity: z.number().nullish(),
  additionalChargeAmount: z.number().nullish(),
  originalAdditionalChargeAmount: z.number().nullish(),
  discount: DiscountValidationSchema.nullish(),
});

export const VariantValidationSchema = z.object({
  name: z.string().nullish(),
  variantId: z.string().nullish(),
  reference: z.string().nullish(),
  selectionType: z.enum(['single', 'multiple', 'quantity']).nullish(),
  selectedOptions: z.array(VariantSelectedOptionSchema).nullish(),
});

const PaymentTaxBreakdownSchema = z.object({
  taxType: z.string().nullish(),
  taxabilityReason: z.string().nullish(),
  taxableAmount: z.number().nullish(),
  taxAmount: z.number().nullish(),
  taxRateDetails: z
    .object({
      country: z.string().nullish(),
      percentageDecimal: z.number().nullish(),
      state: z.string().nullish(),
      taxType: z.string().nullish(),
    })
    .nullish(),
});

const PaymentBillingValidationSchema = z.object({
  name: z.string().nullish().meta({
    description: 'Name from the billing details.',
  }),
  companyName: z.string().nullish(),
  email: z.string().nullish().meta({
    description: 'Email from the billing details.',
  }),
  billingEmail: z.string().nullish(),
  phone: z.string().nullish().meta({
    description: 'Phone number from the billing details.',
  }),
  address: AddressValidationSchema.nullish().meta({
    description: 'Billing address.',
  }),
});

const PaymentShippingValidationSchema = z.object({
  name: z.string().nullish().meta({
    description: 'Name from the shipping details.',
  }),
  phone: z.string().nullish().meta({
    description: 'Phone number from the shipping details.',
  }),
  address: AddressValidationSchema.nullish().meta({
    description: 'Shipping address.',
  }),
});

export const FieldValidationSchema = z.object({
  fieldId: z.string().nullish().meta({
    description: 'Unique field ID.',
  }),
  label: z.string().nullish().meta({
    description: 'Label shown for the field.',
  }),
  value: z.string().nullish().meta({
    description: 'Submitted value for the field.',
  }),
  values: z.array(z.string()).nullish().meta({
    description:
      'Chosen option values of a multi-select field (an option without a value is named by its label), in the order the options are defined. `value` holds the same values joined with ", ". The matching labels, as they were when the answer was written, are in `meta.selectedOptionLabels` and joined in `meta.selectedOptionLabel`. Absent on every other field type.',
  }),
  element: FieldElementValidationEnum.nullish(),
  type: FieldTypeValidationEnum.nullish(),
  meta: z.record(z.string(), z.unknown()).nullish(),
  reference: z.string().nullish().meta({
    description: 'Reference key for the field.',
  }),
});

export const BaseChargeResponseValidationSchema = z.object({
  id: z.string().meta({
    description: 'Unique record ID. Use this for pagination cursors.',
  }),
  status: ChargeStatusValidationEnum.meta({
    description: `Payment status.
    \`paid\`: payment collected in full.
    \`partially_paid\`: some amount collected, but a balance remains.
    \`refunded\`: full refund issued. \`amountPaid\` still shows the original amount, not the net amount after refund. Use \`amountRefunded\` for the refund amount and \`lastRefundAt\` for the refund date.
    \`partial_refund\`: partial refund issued. Use \`amountRefunded\` for the refund amount and \`amountPaid - amountRefunded\` for the net amount retained. \`lastRefundAt\`, \`lastRefundReason\`, and \`lastRefundReasonNote\` describe the most recent refund action.
    \`draft\`: checkout in progress and not yet submitted.
    \`draft_expired\`: checkout started but expired before completion.
    \`created\`: payment record created, but no action taken yet.
    \`pending\`: payment submitted and awaiting processing.
    \`unpaid\`: manual or invoice payment created, but not yet paid. This is expected for invoice-based checkouts and is not a failure state.
    \`failed\`: payment attempt was rejected. Check \`paymentError\` for the most recent error message when present.
    \`canceled\`: payment was canceled.`,
  }),
  orderId: z.string().nullish().meta({
    description: 'Human-readable order number.',
  }),
  orderStatus: ChargeOrderStatusValidationEnum.nullish().meta({
    description: `Order lifecycle status. Valid values are ${Object.values(CHARGE_ORDER_STATUS).join(', ')}.`,
  }),
  customerEmail: z.string().nullish().meta({
    description:
      "Customer's email address (e.g. `andrew@example.com`). Present on all records with a linked customer entity. Null on incomplete checkouts. Searchable via `search`",
  }),
  customerName: z.string().nullish().meta({
    description: 'Customer full name. Searchable via `search`.',
  }),
  customerId: z.string().nullish().meta({
    description: 'Customer  ID.',
  }),
  sellerId: z.string().nullish().meta({
    description: 'Seller ID.',
  }),
  pageId: z.string().nullish().meta({
    description: 'Checkout page ID.',
  }),
  pageSlug: z.string().nullish().meta({
    description: 'Page URL slug. This is a URL fragment, not a human-friendly title.',
  }),
  clientIp: z.string().nullish().meta({
    description: 'Customer IP address.',
  }),
  currency: z.string().nullish().meta({
    description: 'Three-letter currency code.',
  }),
  amount: z.number().meta({
    type: 'number',
    description: 'Total amount billed for the record, in the smallest currency unit.',
  }),
  amountPaid: z.number().nullish().meta({
    type: 'number',
    description:
      'Amount collected toward `amount`, in the smallest currency unit. Refunded records retain their original `amountPaid` value; use `amountRefunded` to determine the refunded portion.',
  }),
  amountDue: z.number().nullish().meta({
    type: 'number',
    description: 'Outstanding amount still due, in the smallest currency unit.',
  }),
  amountRefunded: z.number().nullish().meta({
    description:
      'Total amount refunded, in the smallest currency unit. Present on all records and `0` when no refund has been issued. NOTE: For records created before 2026-01-22T06:43:28.000Z, some refunds may have incomplete metadata and show `amountRefunded: 0`.',
  }),
  amountUsd: z.number().nullish().meta({
    description:
      'The total amount in USD. Calculated using the exchange rate at the time of the transaction.',
  }),
  amountExcludingTax: z.number().nullish().meta({
    description: 'The total amount excluding tax.',
  }),
  taxAmount: z.number().nullish().meta({
    description: 'The total tax amount.',
  }),
  taxBreakdown: z.array(PaymentTaxBreakdownSchema.nullish()),
  taxSource: TaxSourceValidationSchema.nullish().meta({
    description: TAX_SOURCE_FIELD_DESCRIPTION,
  }),
  taxRates: z
    .array(HistoricalTaxRateSnapshotValidationSchema)
    .nullish()
    .meta({ description: TAX_RATES_FIELD_DESCRIPTION }),
  stripeTaxCalculationId: z.string().nullish(),
  stripeTaxTransactionId: z.string().nullish(),
  dynamicPrice: z.number().nullish().meta({
    description:
      'The dynamic price controlled by query params at checkout that overrides the default price configured on the page.',
  }),
  fees: z
    .array(
      z.object({
        name: z.string().nullish().meta({
          description: 'Fee name.',
        }),
        amount: z.number().nullish().meta({
          description:
            'Fee amount in the smallest currency unit. For a fee with both a percentage and a fixed amount, the sum of `percentageAmount` and `fixedAmount`.',
        }),
        percentage: z.number().nullish().meta({
          description: 'Fee percentage when applicable.',
        }),
        percentageAmount: z.number().nullish().meta({
          description:
            'The part of `amount` from the percentage, in the smallest currency unit. Only set on a fee with both a percentage and a fixed amount. Can be 0, e.g. when a 100% coupon leaves nothing for the percentage to apply to.',
        }),
        fixedAmount: z.number().nullish().meta({
          description:
            'The fixed part of `amount`, in the smallest currency unit, already multiplied by quantity or tickets when the fee multiplies. Only set on a fee with both a percentage and a fixed amount.',
        }),
      }),
    )
    .nullish()
    .meta({
      description: 'Array of fee objects.',
    }),
  processingFeeContribution: z
    .object({
      name: z
        .string()
        .nullish()
        .meta({ description: 'Name shown on the receipt, frozen at payment.' }),
      amount: z
        .number()
        .nullish()
        .meta({ description: 'Contribution in the smallest currency unit.' }),
      percentage: z
        .number()
        .nullish()
        .meta({ description: 'Percentage used for the contribution.' }),
      percentageAmount: z.number().nullish().meta({ description: 'The part from the percentage.' }),
      fixedAmount: z.number().nullish().meta({ description: 'The fixed part.' }),
    })
    .nullish()
    .meta({
      description:
        'What the customer chose to add to cover the processing fee, worked out on the total with tax. Not one of `fees`.',
    }),
  billing: PaymentBillingValidationSchema.nullish().meta({
    description: 'Billing details, including name, email, phone, and address.',
  }),
  shipping: PaymentShippingValidationSchema.nullish().meta({
    description: 'Shipping details, including name, phone, and address. Often empty.',
  }),
  fields: z.array(FieldValidationSchema).nullish().meta({
    description: 'Submitted form field responses.',
  }),
  coupon: z
    .object({
      id: z.string().nullish(),
      label: z.string().nullish().meta({
        description: 'Coupon label.',
      }),
      code: z.string().nullish().meta({
        description: 'Coupon code.',
      }),
      amountOff: z.number().nullish().meta({
        description:
          'Fixed amount off configured on the coupon. Cannot be combined with `percentOff`.',
      }),
      percentOff: z.number().nullish().meta({
        description:
          'Percentage off configured on the coupon. Cannot be combined with `amountOff`.',
      }),
      discount: z.number().nullish().meta({
        description: 'Total discount actually applied to this record.',
      }),
      // @TODO uncomment when coupon ticket types goes live
      // ticketTypes: z.array(ObjectIdStringValidationSchema).nullish(),
      // pages: z.array(ObjectIdStringValidationSchema).nullish(),
      // timesRedeemed: z.number().nullish(),
      // maxRedemptions: z.number().nullish(),
      // redeemBy: z.iso.datetime().nullish()
    })
    .nullish()
    .meta({
      description:
        'Coupon details when a coupon was applied. `amountOff` is the fixed discount configured on the coupon, `percentOff` is the configured percentage discount, and `discount` is the total discount actually applied to this record.',
    }),
  discount: DiscountValidationSchema.nullish(),
  livemode: z.boolean().nullish().meta({
    description: 'Whether this is a real transaction.',
  }),
  paymentError: z.string().nullish().meta({
    description:
      'Last payment-specific error message. This field is not guaranteed to be set and persists after a later successful payment, so check `status` before interpreting `paymentError`.',
  }),
  paymentMethod: PaymentMethodValidationSchema.nullish().meta({
    description:
      'Payment method details, such as `gateway`, `method`, `brand`, `last4`, `country`, and `funding`. This may be absent when a payment method was never initiated.',
  }),
  paymentOption: z
    .object({
      type: ChargePaymentOptionTypeValidationEnum.nullish(),
      name: z.string().nullish(),
      description: z.string().nullish(),
      instructions: z.string().nullish(),
      partialAmount: z.number().nullish(),
      manualType: ChargePaymentOptionManualTypeValidationEnum.nullish(),
    })
    .nullish()
    .meta({
      description:
        'Payment option details. `type` is `full` for standard payments and `manual` for invoice or offline flows; when `type` is `manual`, a `status: unpaid` record is expected and does not indicate a failure.',
    }),
  locale: z.string().nullish().meta({
    description: "Customer's browser locale.",
  }),
  sessionId: z.string().nullish().meta({
    description: 'Unique session ID.',
  }),
  userAgent: z.string().nullish().meta({
    description: "Customer's browser user agent string.",
  }),
  screenWidth: z.number().nullish().meta({
    description: "Customer's screen width in pixels.",
  }),
  visitId: z.string().nullish().meta({
    description: 'Unique visit ID to track a customer across multiple sessions.',
  }),
  queryParameters: z.record(z.string(), z.string()).nullish().meta({
    description: 'URL query parameters captured from the page link.',
  }),
  stripePaymentIntentId: z.string().nullish(),
  stripeChargeId: z.string().nullish(),
  upsell: z.boolean().nullish().meta({
    description:
      'Whether this record is an upsell. Upsell records are included in results by default.',
  }),
  upsellPageId: z.string().nullish().meta({
    description:
      'For an upsell record, the ID of the page the buyer paid on before accepting the upsell (the start of the funnel).',
  }),
  isComplimentary: z.boolean().nullish().meta({
    description:
      'Whether this is a complimentary booking: issued by the merchant at no charge, recorded as `paid` with `amount`, `amountPaid` and `amountDue` of 0 while the ticket lines keep their face value. Always false on payments.',
  }),
  complimentaryDiscountAmount: z.number().nullish().meta({
    description:
      'On a complimentary booking, the discount that brought the total to 0, in the smallest currency unit. Equal to the pre-discount subtotal of the ticket lines. Absent otherwise.',
  }),
  upsellChargeId: z.string().nullish().meta({
    description: 'For an upsell record, the ID of the payment made on the page before the upsell.',
  }),
  upsellSubscriptionId: z.string().nullish().meta({
    description:
      'For an upsell record, the ID of the subscription created on the page before the upsell.',
  }),
  invoiceId: z.string().nullish().meta({
    description:
      'Generated invoice ID when an invoice exists. This confirms that an invoice was created, but not that the invoice document itself is available here.',
  }),
  transactionIds: z.array(z.string()).nullish(),
  paidAt: z.iso.datetime().nullish().meta({
    description:
      'When the payment was paid in full. For an invoice or cash payment this is when the merchant recorded the payment, which can be long after `createdAt`. Empty until fully paid, including a deposit with a balance still due.',
  }),
  lastRefundAt: z.iso.datetime().nullish().meta({
    description: 'Timestamp of the most recent refund action.',
  }),
  lastRefundReason: z.string().nullish().meta({
    description: 'Reason code for the most recent refund action.',
  }),
  lastRefundReasonNote: z.string().nullish().meta({
    description:
      'Free-text note for the most recent refund action. Refund reason fields reflect only the most recent refund action, not the full refund history.',
  }),
  canceledAt: z.iso
    .datetime()
    .nullish()
    .meta({ type: 'string', description: 'The date this was canceled.' }),
  canceledBy: z.string().nullish().meta({
    type: 'string',
    description:
      'The userId that canceled this. If this is a booking, tickets were canceled in addition.',
  }),
  canceledByApiKey: z.string().nullish().meta({
    description:
      'ID of the API key that canceled this, when it was canceled through the seller API. The key may have been deleted since; `canceledByApiKeyName` keeps its name.',
  }),
  canceledByApiKeyName: z.string().nullish().meta({
    description:
      'Name of the API key that canceled this, as it was at the time. Kept when the key is deleted later.',
  }),
  canceledReason: z
    .string()
    .nullish()
    .meta({ type: 'string', description: 'The reason for the cancellation' }),
  archivedAt: z.iso.datetime().nullish().meta({
    type: 'string',
    description:
      'When the merchant archived this, hiding it from their default bookings list in the dashboard. Archiving changes nothing else: the payment, tickets and `orderStatus` stay as they were. Absent unless archived.',
  }),
  archivedBy: z.string().nullish().meta({
    type: 'string',
    description: 'The userId of the team member who archived this. Absent unless archived.',
  }),
  isAbandoned: z.boolean().nullish().meta({
    description: 'Whether the checkout flow was abandoned.',
  }),
  recoveredAt: z.iso
    .datetime()
    .openapi({
      description:
        'The date at which the customer recovered their checkout through cart abandonment.',
    })
    .nullish(),
  abandonmentStatus: z
    .enum(['abandoned', 'recovered'])
    .nullish()
    .openapi({
      description:
        "'abandoned': checkout was started but not completed and not recovered yet. 'recovered': was abandoned but the customer returned and paid. Absent when the charge is not an abandoned cart.",
    })
    .meta({
      description:
        "'abandoned': checkout was started but not completed and not recovered yet. 'recovered': was abandoned but the customer returned and paid. Absent when the charge is not an abandoned cart.",
    }),
  abandonedCartEmailStatus: ChargeAbandonmentEmailStatusValidationEnum.nullish().meta({
    description: `The status of the abandonment email sent for this charge.
    \`pending\`: No email has been sent. Stays as pending if isAbandoned is false.
    \`first_sent\`: The first abandonment email has been sent.
    \`second_sent\`: The second abandonment email has been sent.
    \`third_sent\`: The third abandonment email has been sent.
    \`skipped\`: The abandonment email processed was skipped.`,
  }),
  abandonedCartEmailSentAt: z.iso.datetime().nullish().meta({
    description: 'Timestamp of the most recent abandoned-cart recovery email, if applicable.',
  }),
  createdAt: z.iso.datetime().meta({
    description: 'ISO 8601 timestamp when this record was created.',
  }),
  updatedAt: z.iso.datetime().meta({
    description: 'ISO 8601 timestamp when this record was last updated.',
  }),
});
